import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { PopupData, Service } from 'webapp-types';
import { DeliveryType, GoalStatus } from 'webapp/enums/enums';
import { getCurrentFormattedTime, getFormattedTime, getUnixTimestamp } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormDatetimePickerComponent from 'webapp/components/form/datetime-picker';
import FormInputDecimalComponent from 'webapp/components/form/input-decimal';
import FormSelectComponent from 'webapp/components/form/select';
import FormTextareaComponent from 'webapp/components/form/textarea';
import GoalLogsTableComponent from 'webapp/components/goal/logs/table';
import GoalNotificationsSettingsComponent from 'webapp/components/goal/notifications/settings';
import GoalsProgressComponent from 'webapp/components/goals/progress';
import GoalsStatusComponent from 'webapp/components/goals/status/index';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

import { MAX_TARGET_VALUE_LENGTH } from 'webapp/models-custom/goal';
import type GoalLog from 'webapp/models-custom/goal-log';

export default class GoalPopupUpdateProgress extends PopupInnerModalComponent<PopupData.Goal.UpdateProgress> {
	@service('popup') declare popupService: Service.Popup;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/goal-logs') declare mmGoalLogsService: Service.ModelManager.GoalLogs;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	@tracked date!: string;

	@tracked isGoalLogsLoading = false;
	@tracked updateInputDecimalValue = false;
	@tracked currentMessage = '';
	@tracked model!: GoalLog;
	@tracked selectedStatus = GoalStatus.ON_TRACK;
	@tracked selectedId: string | null = null;

	maxTargetValueLength = MAX_TARGET_VALUE_LENGTH;
	statusOptions = [
		GoalStatus.ON_TRACK,
		GoalStatus.AT_RISK,
		GoalStatus.OFF_TRACK,
		GoalStatus.ACHIEVED,
		GoalStatus.PARTIAL,
		GoalStatus.MISSED,
	].map((status, idx) => ({
		value: status,
		label: this.getStatusText(status),
		group: idx < 3 ? 'Open' : 'Complete',
	}));

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const logToEdit = this.popupData.logToEdit;
		if (logToEdit) {
			this.editGoalLog(logToEdit);
		} else {
			this.resetData();
		}

		next(() => {
			void this.loadLogs();
		});
	}

	get goalModel() {
		return this.popupData.model;
	}

	get goalLogs() {
		return this.mmGoalLogsService.getLogsForGoal(this.goalModel.id!);
	}

	get isEditing() {
		return !this.model.isNew;
	}

	get isGoalProgressAutomatic() {
		return this.goalModel.isAutomaticGoal || this.goalModel.isSubgoalsProgressGoal;
	}

	get dateTimeFormat(): string {
		return `${this.accountService.accountDateFormat} HH:mm:ss`;
	}

	get currentValueString() {
		return this.model.value?.toString();
	}

	get recipientsCount() {
		return this.model.recipients.length + this.model.slackChannels.length;
	}

	get recipientsString() {
		return this.model.recipients.join(', ');
	}

	get statusText() {
		return this.goalModel.primaryDataModel?.statusText;
	}

	get statusClass() {
		return this.goalModel.primaryDataModel?.statusClass;
	}

	get defaultSourceTimezone() {
		return this.mmSourcesService.defaultSource?.timezone || 'Etc/UTC';
	}

	get isDateBeforeToday() {
		return moment
			.tz(this.date, this.dateTimeFormat, this.defaultSourceTimezone)
			.isBefore(moment().startOf('day'));
	}

	@action
	updateOnOutsideValueChange() {
		this.updateInputDecimalValue = true;

		next(() => {
			this.updateInputDecimalValue = false;
		});
	}

	@action
	onValueChange(value: number | null) {
		if (value !== null) {
			this.model.value = value;
		}
	}

	@action
	onStatusChange(value: number | null) {
		const status = value as GoalStatus;
		if (status !== null) {
			this.model.status = status;
			this.selectedStatus = status; // stringifying enum bcos of bug in select component
		}
	}

	@action
	onDateChange(value: string) {
		if (value !== this.date) {
			this.date = value;
		}
	}

	@action
	onDescriptionChange(value: string) {
		this.model.message = value;
	}

	@action
	toggleIsDeliveryOn() {
		this.model.isDeliveryOn = !this.model.isDeliveryOn;
	}

	@action
	revertManualGoalStatus() {
		void this.mmGoalsService.requestStatusRevert(this.goalModel.id!);
	}

	@action
	async onSubmit() {
		if (!this.model.validate()) {
			return;
		}

		this.submitInProgress = true;

		const date = this.isDateBeforeToday
			? moment(this.date, this.dateTimeFormat).endOf('day').format(this.dateTimeFormat)
			: this.date;

		this.model.created = getUnixTimestamp(
			date,
			this.defaultSourceTimezone,
			this.dateTimeFormat,
		);
		this.model.message = this.currentMessage || null;

		await this.mmGoalLogsService.createOrUpdateGoalLog(this.model);

		this.resetForm();

		this.submitInProgress = false;
	}

	@action
	editGoalLog(goalLog: GoalLog) {
		this.selectedId = goalLog.id;

		this.model = this.mmGoalLogsService.createModel(goalLog.model);
		this.selectedStatus = this.model.status;
		this.date = getFormattedTime(
			goalLog.created,
			this.defaultSourceTimezone,
			this.dateTimeFormat,
		);
		this.currentMessage = goalLog.isPredefinedMessage ? '' : goalLog.message || '';

		// when editing, turn off the delivery
		this.model.isDeliveryOn = false;
	}

	@action
	async onDeleteLog(goalLog: GoalLog) {
		await this.mmGoalLogsService.deleteGoalLog(goalLog);
		this.resetForm();
	}

	@action
	cancelEditing() {
		this.resetForm();
	}

	resetData() {
		this.date = getCurrentFormattedTime(this.defaultSourceTimezone, this.dateTimeFormat);
		this.model = this.mmGoalLogsService.createNewModel({
			status: GoalStatus.ON_TRACK,
			goalId: this.goalModel.numericId!,
			delivery: [DeliveryType.EMAIL],
		});
		this.selectedStatus = GoalStatus.ON_TRACK;
	}

	resetForm() {
		this.clearSelectedId();
		this.resetData();
		next(() => {
			this.scheduleReposition();
		});
		this.currentMessage = '';
	}

	async loadLogs() {
		this.isGoalLogsLoading = true;
		await this.mmGoalLogsService.requestGoalLogs(this.goalModel.id!);
		this.isGoalLogsLoading = false;
		next(() => {
			this.scheduleReposition();
		});
	}

	clearSelectedId() {
		this.selectedId = null;
	}

	private getStatusText(status: GoalStatus) {
		return this.mmGoalsService.getStatusText(status);
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="OkRs" @size="lg" />
			Update goal progress
		</div>

		<div
			class="content {{if this.isGoalProgressAutomatic 'automatic'}}"
			{{didUpdate this.updateOnOutsideValueChange this.model.value}}
		>
			<div class="goal-header">
				<div class="goal-info">
					<h3>{{this.goalModel.title}}</h3>
					<div class="status">
						{{#if this.goalModel.primaryDataModel.status}}
							<GoalsStatusComponent
								@status={{this.goalModel.primaryDataModel.status}}
							/>
						{{else}}
							No status yet
						{{/if}}
					</div>
				</div>

				<div class="goal-progress {{this.goalModel.primaryDataModel.currentProgressClass}}">
					{{#if this.goalModel.primaryDataModel}}
						<GoalsProgressComponent
							@model={{this.goalModel}}
							@goalData={{this.goalModel.primaryDataModel}}
							@size="large"
						/>
					{{/if}}
				</div>
			</div>

			<div class="white-ct">
				{{#if this.goalModel.statusOverride}}
					<div class="status-manual">
						<CommonUiIconComponent @name="InfoFill" @size="sm" />
						<p>
							Status won't auto-update because it was manually set.
							{{#if this.goalModel.canEdit}}
								<a
									role="button"
									class="link revert-link"
									{{on "click" this.revertManualGoalStatus}}
								>
									Revert
								</a>
							{{/if}}
						</p>
					</div>
				{{/if}}
				<div class="form-row">
					{{#if this.goalModel.isManualGoal}}
						<div
							class="form-column manual-input
								{{if
									this.goalModel.isHistoryValuesPercentFormat
									'input-with-percent'
								}}"
						>
							<span class="label">Current value</span>
							<FormInputDecimalComponent
								@value={{this.currentValueString}}
								@onChange={{this.onValueChange}}
								@forceUpdateFromValue={{this.updateInputDecimalValue}}
								@placeholder="Current value"
								@maxLength={{this.maxTargetValueLength}}
								@isDisabled={{this.submitInProgress}}
							/>
							{{#if this.goalModel.isHistoryValuesPercentFormat}}
								<span class="percent">%</span>
							{{/if}}
							<RowErrorComponent @items={{this.model.errors.value}} />
						</div>
					{{/if}}
					<div class="form-column">
						<span class="label">Status</span>
						<FormSelectComponent
							@selected={{this.selectedStatus}}
							@options={{this.statusOptions}}
							@onChange={{this.onStatusChange}}
							@isDisabled={{this.submitInProgress}}
							@isRequired={{true}}
						/>
						<RowErrorComponent @items={{this.model.errors.status}} />
					</div>
					<div class="form-column">
						<span class="label">Date</span>
						<FormDatetimePickerComponent
							@dateFormat={{this.dateTimeFormat}}
							@disableTimePicker={{true}}
							@value={{this.date}}
							@onChange={{this.onDateChange}}
							@placeholder="Date"
							@disableFuture={{true}}
							@hideFloatLabel={{true}}
							@isDisabled={{this.submitInProgress}}
						/>
					</div>
				</div>

				<div class="form-row">
					<div class="update-message">
						<FormTextareaComponent
							name="update-message"
							@value={{this.currentMessage}}
							placeholder="Share your goal progress here (optional)"
							@hideFloatLabel={{true}}
							maxlength="255"
						/>
					</div>
				</div>

				<div class="form-actions">
					<div class="notification-remove">
						<GoalNotificationsSettingsComponent
							@model={{this.model}}
							@toggleDelivery={{this.toggleIsDeliveryOn}}
							@submitInProgress={{this.submitInProgress}}
						/>
						{{#if this.isEditing}}
							<a
								role="button"
								class="button theme-red-outlined"
								{{on "click" (fn this.onDeleteLog this.model)}}
							>
								<CommonUiIconComponent @name="Trash" @size="sm" />
								Delete
							</a>
						{{/if}}
					</div>

					<div class="buttons">
						{{#if this.isEditing}}
							<SubmitButtonComponent
								@text="Cancel"
								@theme="grey-empty"
								@onTrigger={{this.cancelEditing}}
							/>
						{{/if}}
						<SubmitButtonComponent
							@text="Save"
							@extraClass="save"
							@onTrigger={{this.onSubmit}}
							@isDisabled={{this.submitInProgress}}
							@submitInProgress={{this.submitInProgress}}
						/>
					</div>
				</div>
			</div>

			<div class="table-ct">
				<GoalLogsTableComponent
					@model={{this.goalLogs}}
					@isLoading={{this.isGoalLogsLoading}}
					@selectedId={{this.selectedId}}
					@edit={{this.editGoalLog}}
				/>
			</div>
		</div>
	</template>
}
